import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from 'antd';
import api from '../../services';
import StoryTitle from '../../components/StoryTitle';
import ButtonPanelReaction from '../../components/Buttons/ButtonPanelReaction';
import ButtonReactionAdd from '../../components/Buttons/ButtonReactionAdd';
import ContentState from '../../components/Brand/ContentState';
import usePageLayout from '../../hooks/usePageLayout';
import { opinionLink } from '../../utils/opinionPath';
import { feedViewerContext } from '../../utils/searchFeedReturn';
import './StoryStatistic/StoryStatistic.scss';

const { Header, Content, Footer } = Layout;

// This route resolves once, then the ordinary feed owns voting, editing,
// playback, swipes and section return snapshots. No second feed implementation.
export default function StoryFeedEntry() {
  const { storyId: rawId } = useParams();
  const storyId = Number(rawId);
  const navigate = useNavigate();
  const viewer = feedViewerContext();
  const key = JSON.stringify([rawId, viewer]);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ key, status: 'loading' });
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  const header = useRef(null);
  const footer = useRef(null);
  const { contentHeight, contentTop } = usePageLayout(header, footer);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const valid = /^[1-9]\d*$/.test(rawId) && storyId <= 2147483647;
    setResult({ key, status: valid ? 'loading' : 'error' });
    if (valid) api.storyLatestOpinion({ id: storyId }, { signal: controller.signal }).then(response => {
      if (!active || viewer !== feedViewerContext()) return;
      if (!response.success || response.story?.id !== storyId
        || typeof response.story.title !== 'string'
        || (response.reactionId !== null && (!Number.isSafeInteger(response.reactionId) || response.reactionId <= 0))) {
        setResult({ key, status: 'error' });
        return;
      }
      if (response.reactionId === null) {
        setResult({ key, status: 'empty', story: response.story });
        return;
      }
      const link = opinionLink({ reactionId: response.reactionId, story: response.story, storyId, source: 'opinions' });
      navigate(link.to, { replace: true, state: link.state });
    }).catch(() => { if (active) setResult({ key, status: 'error' }); });
    return () => { active = false; controller.abort(); };
  }, [rawId, storyId, viewer, key, attempt, navigate]);

  const current = result.key === key ? result : { status: 'loading' };
  return <Layout className="app-layout story-detail-page">
    <Header ref={header} className="header">
      {current.story && <div className="wrapper-padding"><StoryTitle storyId={storyId} title={current.story.title} /></div>}
    </Header>
    <Content className="content-layot statistic-content" style={{ height: contentHeight, top: contentTop }} aria-busy={current.status === 'loading'}>
      <div className="statistic_wrapper">
        <ContentState status={current.status} emptyKey="opinionsEmpty" onRetry={retry} />
        {current.status === 'empty' && <div className="story-empty-opinion-action"><ButtonReactionAdd storyId={storyId} story={current.story} /></div>}
      </div>
    </Content>
    <Footer ref={footer}><ButtonPanelReaction /></Footer>
  </Layout>;
}
